import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useUserStore } from "../../../store/userStore";
import { useCompaniesStore } from "../../../store/admin/companiesStore";
import {
  useSupportChatStore,
  type ChatParticipant,
} from "../../../store/supportChatStore";

/**
 * Hook centralizado para abrir el chat de soporte desde cualquier parte de la
 * app (TripCard, DriverContent, DriversSection, etc.).
 *
 * Reglas:
 *  - 1:1 con un usuario: si ya existe el chat en el store se redirige. Si no
 *    existe, se crea y se redirige. El backend deduplica por participant_ids,
 *    así que es seguro llamarlo aunque el caller no tenga la conversación
 *    todavía en estado local.
 *  - Chat de viaje: el backend NO deduplica por trip_id (puede haber varios
 *    chats por viaje). Por eso aquí solo buscamos un chat existente por
 *    trip_id; si no existe, el caller debe abrir el NewConversationModal
 *    preconfigurado para que el usuario elija participantes.
 */
export function useOpenChat() {
  const navigate = useNavigate();
  const user = useUserStore((state) => state.user);
  const companyId = useCompaniesStore((state) => state.companyId);

  const conversations = useSupportChatStore((s) => s.conversations);
  const createConversation = useSupportChatStore((s) => s.createConversation);
  const fetchConversations = useSupportChatStore((s) => s.fetchConversations);

  const meAsParticipant = useMemo<ChatParticipant | null>(() => {
    if (!user) return null;
    const display_name =
      `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim() ||
      user.username ||
      user.email ||
      "Usuario";
    return {
      uid: user.id,
      display_name,
      email: user.email ?? null,
    };
  }, [user]);

  const goToConversation = useCallback(
    (conversationId: string) => {
      // Solo navegamos a /support con el id como query param. Es
      // SupportChatPage quien, una vez termine de inicializar y de traer la
      // lista de chats, hará el "click" en la conversación pedida
      // (setActiveConversation). Así evitamos races con el `reset()` que
      // corre en el cleanup del init (incluido el doble-mount de
      // React.StrictMode en dev) y respetamos el flujo:
      //   click -> /support -> fetch chats -> seleccionar el chat pedido.
      navigate(`/support?chat=${encodeURIComponent(conversationId)}`);
    },
    [navigate],
  );

  /**
   * Busca un chat 1:1 con `otherUid` en el estado local.
   */
  const findExistingDirectChat = useCallback(
    (otherUid: string) => {
      if (!user?.id) return undefined;
      return conversations.find(
        (c) =>
          c.type === "one-to-one" &&
          !c.trip_id &&
          c.participant_ids.length === 2 &&
          c.participant_ids.includes(otherUid) &&
          c.participant_ids.includes(user.id),
      );
    },
    [conversations, user?.id],
  );

  /**
   * Busca un chat de viaje por `trip_id` en el estado local.
   */
  const findExistingTripChat = useCallback(
    (tripId: string) => conversations.find((c) => c.trip_id === tripId),
    [conversations],
  );

  /**
   * Abre (o crea) un chat 1:1 con otro usuario y navega al chat.
   * Devuelve la conversación abierta o `null` si no fue posible.
   */
  const openDirectChatWithUser = useCallback(
    async (other: ChatParticipant): Promise<string | null> => {
      if (!user?.id || !companyId || !meAsParticipant) return null;
      if (!other?.uid || other.uid === user.id) return null;

      // Fast path: existente en estado local.
      const existing = findExistingDirectChat(other.uid);
      if (existing) {
        goToConversation(existing.id);
        return existing.id;
      }

      try {
        const id = await createConversation({
          type: "one-to-one",
          participants: [meAsParticipant, other],
          company_id: companyId,
        });
        goToConversation(id);
        return id;
      } catch (err) {
        console.error("[useOpenChat] Error opening direct chat:", err);
        return null;
      }
    },
    [
      user?.id,
      companyId,
      meAsParticipant,
      findExistingDirectChat,
      createConversation,
      goToConversation,
    ],
  );

  /**
   * Intenta abrir un chat de viaje existente. Si lo encuentra navega y
   * devuelve `true`. Si no existe, refresca conversaciones una vez y vuelve a
   * intentar. Si sigue sin existir, devuelve `false` para que el caller abra
   * el modal de creación con el viaje preseleccionado.
   */
  const tryOpenExistingTripChat = useCallback(
    async (tripId: string): Promise<boolean> => {
      if (!user?.id || !companyId || !tripId) return false;

      let existing = findExistingTripChat(tripId);

      // Si no tenemos el chat localmente y nunca cargamos la lista (p.ej. el
      // usuario aún no entró a /support), refrescamos antes de decidir crear
      // uno nuevo para evitar duplicados.
      if (!existing && conversations.length === 0) {
        try {
          await fetchConversations();
        } catch (err) {
          console.error(
            "[useOpenChat] Error refreshing conversations:",
            err,
          );
        }
        existing = useSupportChatStore
          .getState()
          .conversations.find((c) => c.trip_id === tripId);
      }

      if (existing) {
        goToConversation(existing.id);
        return true;
      }
      return false;
    },
    [
      user?.id,
      companyId,
      conversations.length,
      findExistingTripChat,
      fetchConversations,
      goToConversation,
    ],
  );

  return {
    openDirectChatWithUser,
    tryOpenExistingTripChat,
    goToConversation,
    isReady: !!user?.id && !!companyId && !!meAsParticipant,
  };
}
